import type { User } from "@supabase/supabase-js";

export const STANDARD_MONTHLY_FREE_REQUEST_LIMIT = 50;
export const ANONYMOUS_MONTHLY_FREE_REQUEST_LIMIT = 10;
export const CARD_ON_FILE_MONTHLY_FREE_REQUEST_LIMIT = 100;

export type FreeTierKind = "standard" | "anonymous" | "custom";

export type AccountFreeTierFields = {
  free_tier_kind: FreeTierKind | null;
  monthly_free_request_limit: number | null;
};

type FreeTierResolutionOptions = {
  hasActivePaymentMethod?: boolean;
};

function isAutoManagedFreeTier(account: AccountFreeTierFields) {
  const limit = normalizeMonthlyFreeRequestLimit(account.monthly_free_request_limit);

  return (
    (account.free_tier_kind === "anonymous" &&
      limit === ANONYMOUS_MONTHLY_FREE_REQUEST_LIMIT) ||
    (account.free_tier_kind === "standard" &&
      limit === STANDARD_MONTHLY_FREE_REQUEST_LIMIT) ||
    (account.free_tier_kind === "custom" &&
      limit === CARD_ON_FILE_MONTHLY_FREE_REQUEST_LIMIT)
  );
}

export function resolveFreeTierForUser(
  user: Pick<User, "is_anonymous">,
  options: FreeTierResolutionOptions = {},
) {
  if (user.is_anonymous) {
    return {
      freeTierKind: "anonymous" as const,
      monthlyFreeRequestLimit: ANONYMOUS_MONTHLY_FREE_REQUEST_LIMIT,
    };
  }

  if (options.hasActivePaymentMethod) {
    return {
      freeTierKind: "custom" as const,
      monthlyFreeRequestLimit: CARD_ON_FILE_MONTHLY_FREE_REQUEST_LIMIT,
    };
  }

  return {
    freeTierKind: "standard" as const,
    monthlyFreeRequestLimit: STANDARD_MONTHLY_FREE_REQUEST_LIMIT,
  };
}

export function shouldSyncManagedFreeTier(
  user: Pick<User, "is_anonymous">,
  account: AccountFreeTierFields,
  options: FreeTierResolutionOptions = {},
) {
  const target = resolveFreeTierForUser(user, options);
  const currentLimit = normalizeMonthlyFreeRequestLimit(
    account.monthly_free_request_limit,
  );

  if (
    account.free_tier_kind === target.freeTierKind &&
    currentLimit === target.monthlyFreeRequestLimit
  ) {
    return false;
  }

  if (user.is_anonymous === true) {
    return isAutoManagedFreeTier(account);
  }

  if (account.free_tier_kind === "anonymous") {
    return true;
  }

  if (options.hasActivePaymentMethod) {
    return isAutoManagedFreeTier(account);
  }

  return isAutoManagedFreeTier(account);
}

export function getCurrentUtcMonthRange(now = new Date()) {
  const start = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0),
  );
  const end = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0, 0),
  );

  return { start, end };
}

export function normalizeMonthlyFreeRequestLimit(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.floor(parsed);
}

export function summarizeMonthlyFreeUsage(
  account: Pick<AccountFreeTierFields, "monthly_free_request_limit"> & {
    credits_balance: number | string | null;
  },
  monthlyUsedRequests: number,
) {
  const limit = normalizeMonthlyFreeRequestLimit(
    account.monthly_free_request_limit,
  );
  const used = Math.max(0, Math.floor(Number(monthlyUsedRequests) || 0));
  const remaining = Math.max(limit - used, 0);
  const creditsBalance = Number(account.credits_balance || 0);

  return {
    limit,
    used,
    remaining,
    enforcedWhenNoCredit: limit > 0,
    appliesBeforeBalance: true,
    requiresCreditsNow: remaining === 0 && creditsBalance <= 0,
  };
}
